import { clsx } from 'clsx';
import { resolveEmotion, type MessageSentiment, type ValenceBand } from './emotionConfig';

/**
 * Below this, a reading is too weak to colour. Phase 8 downgrades rather than
 * hides: the chip still renders, but in the neutral stone treatment, so a weak
 * signal never looks like a confident one.
 */
const MIN_CONFIDENCE = 0.4;

interface MessageEmotionChipProps {
  /** Present ONLY on scored customer messages (`message.metadata.sentiment`). */
  sentiment?: MessageSentiment;
  className?: string;
}

/**
 * Emotion chip for a single scored customer message.
 *
 * Square, mono, and named — it shows the emotion (`FRUSTRATION`, `GRATITUDE`,
 * `NEUTRAL`), not a valence word and not the score; the score lives in the
 * title. The emoji is gone: a serif-and-mono system shouldn't pull in an emoji
 * font for a 10px mark, so a single `◔` carries it instead.
 *
 * Neutral renders (the designer's call) in quiet stone, so only real signal
 * takes colour. Anger gets burgundy rather than terracotta — it is the one
 * reading that should look worse than "frustrated".
 */
const bandClasses: Record<ValenceBand, string> = {
  negative:
    'text-[var(--co-terracotta)] border-[var(--co-terracotta)] bg-[var(--co-terracotta-wash)]',
  positive: 'text-[var(--co-green)] border-[var(--co-green)] bg-[var(--co-green-wash)]',
  neutral: 'text-[var(--co-ink-3)] border-[var(--co-line-subtle)] bg-[var(--co-surface)]',
};

const ANGER_CLASSES =
  'text-[var(--co-burgundy)] border-[var(--co-burgundy)] bg-[var(--co-burgundy-wash)]';

export function MessageEmotionChip({ sentiment, className }: MessageEmotionChipProps) {
  // Never fabricate a reading: a ticket from before scoring existed shows no
  // chips at all, and that is correct.
  if (!sentiment || typeof sentiment.score !== 'number') return null;

  const config = resolveEmotion(sentiment.dominantEmotion);
  const lowConfidence =
    typeof sentiment.confidence === 'number' && sentiment.confidence < MIN_CONFIDENCE;

  const isAnger = String(sentiment.dominantEmotion || '').toLowerCase() === 'anger';
  const tone = lowConfidence
    ? bandClasses.neutral
    : isAnger
      ? ANGER_CLASSES
      : bandClasses[config.valenceBand];

  const title =
    `${config.label} · score ${sentiment.score.toFixed(2)}` +
    (lowConfidence ? ' · low confidence' : '');

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-[5px] rounded-[2px] border px-[5px] py-[3px] font-mono text-[9.5px] font-medium uppercase leading-none tracking-[.14em]',
        tone,
        className,
      )}
      title={title}
    >
      <span aria-hidden="true">◔</span>
      <span>{config.label}</span>
    </span>
  );
}

export default MessageEmotionChip;
